/**
 * Ingest API Client
 * 
 * Typed functions for all ingestion endpoints.
 * Implements endpoints from API_CONTRACTS.md.
 */

import { apiClient, getAuthHeaders } from './client';
import type {
  IngestURLResponse,
  IngestTextResponse,
  IngestBulkURLResponse,
  NotesReviewResponse,
} from '../types/api';

/**
 * Ingest a URL (YouTube or Instagram)
 * POST /ingest/url
 */
export async function url(urlString: string): Promise<IngestURLResponse> {
  return apiClient.post<IngestURLResponse>('/ingest/url', { url: urlString });
}

/**
 * Ingest multiple Instagram/Reel URLs
 * POST /ingest/url/bulk
 */
export async function bulkUrls(urls: string[]): Promise<IngestBulkURLResponse> {
  return apiClient.post<IngestBulkURLResponse>('/ingest/url/bulk', { urls });
}

export interface ReelQueueState {
  paused: boolean;
  queued_count: number;
  worker_count?: number;
  message?: string;
}

/**
 * Get local Reel queue state
 * GET /ingest/reels/queue
 */
export async function getReelQueueState(): Promise<ReelQueueState> {
  return apiClient.get<ReelQueueState>('/ingest/reels/queue');
}

/**
 * Pause local Reel queue after the active item finishes
 * POST /ingest/reels/queue/pause
 */
export async function pauseReelQueue(): Promise<ReelQueueState> {
  return apiClient.post<ReelQueueState>('/ingest/reels/queue/pause', {});
}

/**
 * Resume local Reel queue
 * POST /ingest/reels/queue/resume
 */
export async function resumeReelQueue(): Promise<ReelQueueState> {
  return apiClient.post<ReelQueueState>('/ingest/reels/queue/resume', {});
}

/**
 * Ingest text or voice transcript
 * POST /ingest/text
 */
export async function text(
  textContent: string,
  sourceType: 'voice' | 'text' | 'paste'
): Promise<IngestTextResponse> {
  return apiClient.post<IngestTextResponse>('/ingest/text', {
    text: textContent,
    source_type: sourceType,
  });
}

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.heic', '.heif'];
const DOCUMENT_EXTS = ['.pdf', '.md', '.markdown', '.txt', '.text'];
const MEDIA_EXTS = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v', '.mp3', '.m4a', '.wav', '.ogg', '.opus'];

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

/**
 * Pick the correct ingestion endpoint for a file:
 *   images    -> /ingest/image   (Gemini vision + OCR)
 *   documents -> /ingest/document (PDF / Markdown / text)
 *   audio/video (and anything else) -> /ingest/file (transcription)
 */
export function endpointForFile(fileData: File): string {
  const ext = extOf(fileData.name || '');
  const mime = (fileData.type || '').toLowerCase();
  if (mime.startsWith('image/') || IMAGE_EXTS.includes(ext)) return '/ingest/image';
  if (DOCUMENT_EXTS.includes(ext)) return '/ingest/document';
  if (mime.startsWith('audio/') || mime.startsWith('video/') || MEDIA_EXTS.includes(ext)) return '/ingest/file';
  // Default: treat unknown types as documents (backend validates + rejects).
  return '/ingest/document';
}

async function uploadFileTo(endpointPath: string, fileData: File): Promise<IngestURLResponse> {
  const formData = new FormData();
  formData.append('file', fileData);

  const baseURL = apiClient['baseURL'] || '';
  // Attach the auth token (multipart bypasses apiClient, which would otherwise
  // add it). Do NOT set Content-Type — the browser sets the multipart boundary.
  const authHeaders = await getAuthHeaders();

  try {
    const response = await fetch(`${baseURL}${endpointPath}`, {
      method: 'POST',
      body: formData,
      headers: { ...authHeaders },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const detail = body.detail || body;
      throw new Error(detail.message || detail.error || 'File upload failed');
    }
    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error('Network request failed');
  }
}

/**
 * Ingest any supported file. Routes to the right endpoint by type so documents
 * (PDF/MD/txt) and images (vision+OCR) work, not just audio/video.
 */
export async function file(fileData: File): Promise<IngestURLResponse> {
  return uploadFileTo(endpointForFile(fileData), fileData);
}

/** Explicitly ingest a document (PDF / Markdown / text). */
export async function document(fileData: File): Promise<IngestURLResponse> {
  return uploadFileTo('/ingest/document', fileData);
}

/** Explicitly ingest an image (Gemini vision + OCR). */
export async function image(fileData: File): Promise<IngestURLResponse> {
  return uploadFileTo('/ingest/image', fileData);
}

export async function notesReview(input: {
  text?: string;
  file?: File;
  chatSessionId?: string | null;
}): Promise<NotesReviewResponse> {
  const formData = new FormData();
  if (input.text?.trim()) {
    formData.append('text', input.text.trim());
  }
  if (input.file) {
    formData.append('file', input.file);
  }
  if (input.chatSessionId) {
    formData.append('chat_session_id', input.chatSessionId);
  }

  const baseURL = apiClient['baseURL'] || '';
  const endpoint = `${baseURL}/ingest/notes`;
  const authHeaders = await getAuthHeaders();

  const response = await fetch(endpoint, {
    method: 'POST',
    body: formData,
    headers: { ...authHeaders },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const detail = error.detail || error;
    throw new Error(detail.message || detail.error || 'Notes review failed');
  }

  return response.json();
}
